import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, CircularProgress } from "@mui/material";
import PageShell from "components/bento/PageShell";
import StationSwitcher from "components/StationSwitcher";
import { getMyWhatsappInstances } from "api/whatsapp";
import WhatsAppChat from "scenes/whatsapp-settings/WhatsAppChat";

// Entry point for the "WhatsApp Chat" menu item — per-user instance assignment (see
// User.whatsappInstances, assigned in Users & Roles, same pattern as station assignment).
// Loads the instances assigned to the logged-in user, opens their default one first (like a
// default station), and lets them switch between assigned instances via the header. The actual
// chat UI is the same WhatsAppChat component the WhatsApp settings "Open Chat" button uses —
// here it's given apiBase="mychat" so its requests go through /api/whatsapp-chat (gated by
// instance assignment) instead of the admin-only /api/whatsapp.
const WhatsAppChatMenu = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [instances, setInstances] = useState(null); // null = still loading
  const [loadError, setLoadError] = useState(null);

  const loadInstances = useCallback(async () => {
    setLoadError(null);
    const result = await getMyWhatsappInstances();
    if (result.success) {
      setInstances(result.data || []);
    } else {
      setInstances([]);
      setLoadError(result.message || t("whatsappChat.loadFailed"));
    }
  }, [t]);

  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  // Selection: ?instance= in the URL when it's one of the assigned instances, otherwise the
  // user's default, otherwise the first assigned instance. Not persisted anywhere beyond the
  // URL — same as StationSwitcher's other callers (e.g. Branch Cash).
  const requestedId = searchParams.get("instance");
  const selected = useMemo(() => {
    if (!instances || instances.length === 0) return null;
    if (requestedId) {
      const match = instances.find((i) => i.id === requestedId);
      if (match) return match;
    }
    return instances.find((i) => i.default) || instances[0];
  }, [instances, requestedId]);

  const handleSwitch = useCallback(
    (id) => {
      setSearchParams(id ? { instance: id } : {}, { replace: false });
    },
    [setSearchParams]
  );

  if (instances === null) {
    return (
      <PageShell title={t("whatsappChat.title")}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress color="secondary" />
        </Box>
      </PageShell>
    );
  }

  if (instances.length === 0) {
    return (
      <PageShell title={t("whatsappChat.title")}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 8 }}>
          <Typography sx={{ fontWeight: 700 }}>{t("whatsappChat.noInstanceAssigned")}</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            {loadError || t("whatsappChat.noInstanceAssignedHint")}
          </Typography>
        </Box>
      </PageShell>
    );
  }

  // Only show the switcher when there's an actual choice — matches StationSwitcher's other
  // usages, where a single-option list would just be a button that does nothing.
  const headerExtra =
    instances.length > 1 ? (
      <StationSwitcher
        value={selected?.id}
        options={instances.map((i) => ({ id: i.id, name: i.name }))}
        onChange={handleSwitch}
      />
    ) : null;

  return (
    <WhatsAppChat
      key={selected.id /* full remount on switch — simplest way to reset all chat/message state */}
      groupId={selected.id}
      apiBase="mychat"
      backTo={null}
      headerExtra={headerExtra}
    />
  );
};

export default WhatsAppChatMenu;
